import React, { useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';

export const PINWHEEL_CONFIG = {
  ARM_COUNT: 4,
  POINTS_PER_ARM: 1800,
  RADIAL_EXPONENT: 0.5,
  DOT_RADIUS_MIN: 0.6,
  DOT_RADIUS_GROWTH: 1,
  CURL: 0.2,
  RADIUS: 330,
  ROTATION_PERIOD_SECONDS: 160,
  BASE_OPACITY: 1.495,
  MOBILE_POINTS_PER_ARM: 820,
  CANVAS_SIZE: 720,
};

export const pinwheelRotation = (elapsedSeconds: number) =>
  elapsedSeconds / PINWHEEL_CONFIG.ROTATION_PERIOD_SECONDS * Math.PI * 2;

export const createPinwheelPoints = (count: number) =>
  Array.from({ length: PINWHEEL_CONFIG.ARM_COUNT }, (_, b) =>
    Array.from({ length: count }, () => ({
      b,
      t: Math.pow(Math.random(), PINWHEEL_CONFIG.RADIAL_EXPONENT),
      j: Math.random() * 2 - 1,
    }))
  );

export const pinwheelPointPosition = (b: number, t: number, j: number, rotation: number) => {
  const u = (j + 1) / 2;
  const v = 1 - u;
  // Sample the logo blade's curved outer edge, then fill from its offset hub.
  const edgeX = -10 * v ** 3 + 30 * v ** 2 * u + 90 * v * u ** 2 + 40 * u ** 3;
  const edgeY = -42 * v ** 3 - 144 * v ** 2 * u - 120 * v * u ** 2 - 26 * u ** 3;
  const scale = PINWHEEL_CONFIG.RADIUS / 46;
  const x = -(-4 + t * (edgeX + 4)) * scale;
  const y = (-4 + t * (edgeY + 4)) * scale;
  const angle = b * Math.PI / 2 + rotation - t * PINWHEEL_CONFIG.CURL;
  return {
    x: PINWHEEL_CONFIG.CANVAS_SIZE / 2 + x * Math.cos(angle) - y * Math.sin(angle),
    y: PINWHEEL_CONFIG.CANVAS_SIZE / 2 + x * Math.sin(angle) + y * Math.cos(angle),
  };
};

const PinwheelBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointsRef = useRef<ReturnType<typeof createPinwheelPoints> | null>(null);
  const elapsedRef = useRef(0);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) {
      console.error('MillOS hero: Canvas 2D is unavailable; decorative pinwheel cannot render.');
      return;
    }
    const mobile = window.matchMedia('(max-width:639px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const { CANVAS_SIZE, BASE_OPACITY } = PINWHEEL_CONFIG;
    const pointCount = mobile.matches
      ? PINWHEEL_CONFIG.MOBILE_POINTS_PER_ARM : PINWHEEL_CONFIG.POINTS_PER_ARM;
    let points = pointsRef.current?.[0].length === pointCount
      ? pointsRef.current : createPinwheelPoints(pointCount);
    pointsRef.current = points;
    let pointLimit = points[0].length;
    let colors: string[] = [];
    let frame: number | null = null;
    let previousTime: number | null = null;
    let visible = false;
    let slowFrames = 0;

    const resolveColors = () => {
      const probe = document.createElement('span');
      probe.hidden = true;
      canvas.parentElement?.appendChild(probe);
      colors = [
        theme.palette.secondary.main, theme.palette.primary.main,
        theme.palette.tokens.accentTealDark, theme.palette.primary.dark,
      ].map((color) => {
        probe.style.color = color;
        return getComputedStyle(probe).color;
      });
      probe.remove();
    };
    const draw = () => {
      context.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      const rotation = pinwheelRotation(elapsedRef.current);
      points.forEach((arm, b) => {
        context.fillStyle = colors[b];
        for (let index = 0; index < pointLimit; index++) {
          const { t, j } = arm[index];
          const { x, y } = pinwheelPointPosition(b, t, j, rotation);
          const fade = Math.min(1, t * 4);
          context.globalAlpha = Math.min(1, Math.max(0,
            BASE_OPACITY * (0.10 + 0.32 * (1 - t * 0.45)) * (0.3 + 0.7 * fade)));
          context.beginPath();
          context.arc(x, y, PINWHEEL_CONFIG.DOT_RADIUS_MIN + t * PINWHEEL_CONFIG.DOT_RADIUS_GROWTH, 0, Math.PI * 2);
          context.fill();
        }
      });
      context.globalAlpha = 1;
    };
    const tick = (time: number) => {
      frame = null;
      if (previousTime !== null) elapsedRef.current += (time - previousTime) / 1000;
      previousTime = time;
      const start = performance.now();
      draw();
      slowFrames = performance.now() - start > 8 ? slowFrames + 1 : 0;
      if (slowFrames >= 20 && pointLimit > 250) {
        pointLimit = Math.max(250, Math.floor(pointLimit * 0.75));
        slowFrames = 0;
      }
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      previousTime = null;
      if (visible && document.visibilityState !== 'hidden' && !reduced.matches) {
        frame = requestAnimationFrame(tick);
      }
    };
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = CANVAS_SIZE * dpr;
      canvas.height = CANVAS_SIZE * dpr;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      resolveColors();
      draw();
    };
    const changeDensity = () => {
      points = createPinwheelPoints(mobile.matches
        ? PINWHEEL_CONFIG.MOBILE_POINTS_PER_ARM : PINWHEEL_CONFIG.POINTS_PER_ARM);
      pointsRef.current = points;
      pointLimit = points[0].length;
      resize();
    };
    const motionChanged = () => { draw(); sync(); };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    });
    // Observe the hero, not the overflowing decorative canvas.
    observer.observe(canvas.closest('section') ?? canvas);
    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', sync);
    mobile.addEventListener('change', changeDensity);
    reduced.addEventListener('change', motionChanged);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', sync);
      mobile.removeEventListener('change', changeDensity);
      reduced.removeEventListener('change', motionChanged);
    };
  }, [theme]);

  return (
    <Box component="canvas" ref={canvasRef} aria-hidden="true" data-pinwheel
      sx={{
        position: 'absolute', width: PINWHEEL_CONFIG.CANVAS_SIZE,
        height: PINWHEEL_CONFIG.CANVAS_SIZE, top: 'calc(50% + var(--hero-offset-y, 0px))', left: '50%',
        transform: 'translate(-50%, -50%) scale(0.9945)', pointerEvents: 'none',
        '@media (max-width:639px)': {
          top: 'calc(50% + var(--hero-offset-y, 0px))', transform: `translate(-50%, -50%) scale(${517.14 / PINWHEEL_CONFIG.CANVAS_SIZE})`,
        },
        '@media (max-width:1100px) and (orientation:portrait)': {
          width: 'min(720px, calc(100% - 8px), calc(100svh - 200px))',
          height: 'auto', aspectRatio: '1 / 1',
          top: 'calc(50% + var(--hero-offset-y, 0px) + 12px)',
          transform: 'translate(-50%, -50%)',
        },
      }}
    />
  );
};

export default PinwheelBackground;
